'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CAQSGPaperSectionProps {
  currentLanguage?: string;
}

const CAQSGPaperSection = ({ currentLanguage = 'en' }: CAQSGPaperSectionProps) => {
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donationStep, setDonationStep] = useState<'info' | 'email' | 'success'>('info');
  const [emailInput, setEmailInput] = useState('');

  const handleViewPaper = () => {
    setShowDonateModal(true);
    setDonationStep('info');
  };

  const handleProceedToEmail = () => {
    setDonationStep('email');
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (emailInput.trim()) {
      setDonationStep('success');
    }
  };

  const handleCloseModal = () => {
    setShowDonateModal(false);
    setDonationStep('info');
    setEmailInput('');
  };

  return (
    <>
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-green-950 to-slate-900 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-green-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-400/30 rounded-full text-green-300 text-sm font-semibold mb-4">
              <Icon name="DocumentTextIcon" size={16} />
              Global Published Research Paper — 2026
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Breakthrough Research: CAQSG™ Framework
            </h2>
            <p className="text-green-200/80 text-lg max-w-3xl mx-auto">
              Peer-reviewed research introducing the world's first Correlated AI/Quantum Spectral Graphing system for carbon credit MRV — published globally on SSRN 2026.
            </p>
          </div>

          {/* Paper Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-green-400/30 transition-all duration-300">
              {/* Paper Header */}
              <div className="bg-gradient-to-r from-green-800/50 to-emerald-800/50 p-6 md:p-8 border-b border-white/10">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Paper Icon */}
                  <div className="w-16 h-20 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 border border-white/20">
                    <Icon name="DocumentTextIcon" size={32} className="text-green-300" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 bg-green-500/30 text-green-200 text-xs font-bold rounded">SSRN 2026</span>
                      <span className="px-2 py-1 bg-blue-500/30 text-blue-200 text-xs font-bold rounded">Carbon Science</span>
                      <span className="px-2 py-1 bg-purple-500/30 text-purple-200 text-xs font-bold rounded">Quantum AI</span>
                      <span className="px-2 py-1 bg-yellow-500/30 text-yellow-200 text-xs font-bold rounded">MRV Innovation</span>
                    </div>
                    <h3 className="font-heading font-bold text-xl md:text-2xl text-white mb-2 leading-tight">
                      CAQSG — Correlated AI/Quantum Spectral Graphing for Enhanced Rock Weathering and Carbon Credit MRV
                    </h3>
                    <p className="text-green-200/80 text-sm">
                      <span className="font-semibold text-green-300">Dr. Nupur Mukherjee</span> · GenCarbon ESG Pvt. Ltd. · 2026
                    </p>
                  </div>
                </div>
              </div>

              {/* Paper Body */}
              <div className="p-6 md:p-8">
                {/* Abstract */}
                <div className="mb-6">
                  <h4 className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-3">Abstract</h4>
                  <p className="text-white/75 text-sm leading-relaxed line-clamp-4">
                    This paper introduces CAQSG (Correlated AI/Quantum Spectral Graphing), a novel hybrid Monitoring-Reporting-Verification (MRV) framework designed to enhance carbon credit potential and reduce uncertainty in voluntary carbon markets. CAQSG integrates hyperspectral drone and satellite imagery with graph-Laplacian spectral analysis solved via classical or quantum eigensolvers, enabling sub-hectare resolution identification of carbon sequestration hotspots. The method supports Enhanced Rock Weathering (ERW) layered onto agroforestry, aquaculture, and managed turf systems, aligned with Verra VM0047 v1.1 and Puro.earth ERW Edition 2025 standards.
                  </p>
                </div>

                {/* Key Findings */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { value: '30–40%', label: 'Lower Uncertainty', color: 'text-green-300' },
                    { value: '190–255', label: 'tCO₂e/yr (70 acres)', color: 'text-blue-300' },
                    { value: '12–18 mo', label: 'First Credit Issuance', color: 'text-yellow-300' },
                    { value: '2.5–3×', label: 'Higher Carbon Yields', color: 'text-purple-300' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                      <p className={`font-bold text-lg ${stat.color}`}>{stat.value}</p>
                      <p className="text-white/60 text-xs mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Topics */}
                <div className="mb-8">
                  <h4 className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-3">Key Topics Covered</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'Enhanced Rock Weathering', 'Quantum Eigensolvers', 'Hyperspectral Drone MRV',
                      'Verra VM0047', 'Carbon Sequestration', 'BRSR Reporting',
                      'Golf Course Carbon Credits', 'Steel Slag Valorisation', 'Agroforestry'
                    ].map((topic, i) => (
                      <span key={i} className="px-3 py-1 bg-white/8 border border-white/15 text-white/70 text-xs rounded-full">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between p-5 bg-gradient-to-r from-green-900/50 to-emerald-900/50 rounded-xl border border-green-500/30">
                  <div>
                    <p className="text-white font-semibold text-base">Access Full Research Paper</p>
                    <p className="text-green-300/80 text-sm">Share your email to access the complete paper</p>
                  </div>
                  <button
                    onClick={handleViewPaper}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-base rounded-xl hover:from-green-400 hover:to-emerald-400 shadow-lg hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-105 flex-shrink-0"
                  >
                    <Icon name="EyeIcon" size={20} />
                    View Full Paper — Share Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donate Modal */}
      {showDonateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Icon name="DocumentTextIcon" size={24} />
                  <span className="font-bold text-lg">CAQSG Research Paper</span>
                </div>
                <button onClick={handleCloseModal} className="text-white/80 hover:text-white transition-colors">
                  <Icon name="XMarkIcon" size={24} />
                </button>
              </div>
              <p className="text-green-100 text-sm">Support open science & access full research</p>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {donationStep === 'info' && (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="DocumentTextIcon" size={32} className="text-green-600" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">Access Full Research Paper</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Share your email address to receive the complete CAQSG™ research paper. Dr. Nupur will send it to you within 24 hours.
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 mb-6 border border-green-200">
                    <h4 className="font-semibold text-green-800 text-sm mb-2">What you get:</h4>
                    <ul className="space-y-2">
                      {[
                        'Full 30-page CAQSG research paper (PDF)',
                        'CAQSG™ methodology appendix & worked example',
                        'Carbon credit calculation templates',
                        'Acknowledgement in future publications',
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-green-700">
                          <Icon name="CheckCircleIcon" size={16} className="text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={handleProceedToEmail}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-base rounded-xl hover:from-green-500 hover:to-emerald-500 transition-all duration-300 shadow-lg"
                  >
                    Share Email to Access Paper
                  </button>
                  <p className="text-center text-xs text-gray-500 mt-3">Free access — no payment required</p>
                </>
              )}

              {donationStep === 'email' && (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="EnvelopeIcon" size={32} className="text-green-600" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">Share Your Email</h3>
                    <p className="text-gray-600 text-sm">We'll send the full CAQSG research paper directly to your inbox.</p>
                  </div>

                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="Your email address"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-500 text-sm"
                    />
                    <button
                      type="submit"
                      className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-base rounded-xl hover:from-green-500 hover:to-emerald-500 transition-all duration-300 shadow-lg"
                    >
                      Send Me the Paper
                    </button>
                  </form>
                  <button
                    onClick={() => setDonationStep('info')}
                    className="w-full mt-3 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
                  >
                    ← Back
                  </button>
                </>
              )}

              {donationStep === 'success' && (
                <div className="text-center py-4">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircleIcon" size={40} className="text-green-600" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    Thank you! Dr. Nupur will send the full CAQSG research paper to <strong>{emailInput || 'your email'}</strong> within 24 hours.
                  </p>
                  <button
                    onClick={handleCloseModal}
                    className="px-8 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-500 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CAQSGPaperSection;
